import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { editDocument, getDocumentById } from '../../../services/adminService';
import { toast } from 'react-toastify';
import { usePermissions } from '../../../context/PermissionsContext';

// Utility function to convert blob URL to File
const urlToFile = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const filename = url.split('/').pop() || 'document.pdf';
    return new File([blob], filename, { type: blob.type });
};

function CourseDocument() {
    const { courseId, moduleId, contentId } = useParams();
    const location = useLocation();
    const { documentName, pdfUrl } = location.state || {};
    const { permissions } = usePermissions();
    // console.log(documentName, pdfUrl);


    const decodedCourseId = decodeData(courseId);
    const decodedModuleId = decodeData(moduleId);
    const decodedContentId = decodeData(contentId);

    const [showEditModal, setShowEditModal] = useState(false);
    const [documentData, setDocumentData] = useState({
        name: documentName || 'Untitled Document',
        pdfUrl: pdfUrl || '',
        points: '0'
    });
    const [uploadDoc, setUploadDoc] = useState(null);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({
        documentName: '',
        documentFile: '',
        points: ''
    });

    // Define refreshDocumentData first using useCallback
    const refreshDocumentData = useCallback(async () => {
        if (!decodedContentId) return;

        try {
            const response = await getDocumentById({
                doc_id: decodedContentId
            });

            // console.log('Refreshed Document Data:', response.data);
            
            if (response.success && response.data) {
                setDocumentData({
                    name: response.data.doc_name || 'Untitled Document',
                    pdfUrl: response.data.doc_url || '',
                    points: response.data.earnPoint !== undefined && response.data.earnPoint !== null ? response.data.earnPoint.toString() : (response.data.earning_points !== undefined && response.data.earning_points !== null ? response.data.earning_points.toString() : '0')
                });
            } else {
                console.error('Failed to refresh document data');
            }
        } catch (error) {
            console.error('Error refreshing document data:', error);
        }
    }, [decodedContentId]);

    // Use refreshDocumentData in useEffect
    useEffect(() => {
        // Initial fetch
        refreshDocumentData();

        // Set up periodic refresh (every 30 seconds)
        const refreshInterval = setInterval(refreshDocumentData, 30000);

        // Cleanup interval on component unmount
        return () => clearInterval(refreshInterval);
    }, [refreshDocumentData]); // Only depend on refreshDocumentData since it already depends on decodedContentId

    useEffect(() => {
        // Commented multi-line console.log

        // Validate IDs and document data
        if (!decodedCourseId || !decodedModuleId || !decodedContentId) {
            console.error('Missing or invalid IDs:', {
                courseId: decodedCourseId,
                moduleId: decodedModuleId,
                contentId: decodedContentId
            });
            setError('Invalid document parameters');
        }

        if (!pdfUrl) {
            console.warn('No PDF URL provided in navigation state');
        }

        // Update document data if received from navigation
        if (documentName || pdfUrl) {
            setDocumentData({
                name: documentName || 'Untitled Document',
                pdfUrl: pdfUrl || ''
            });
        }
    }, [courseId, moduleId, contentId, decodedCourseId, decodedModuleId, decodedContentId, documentName, pdfUrl, location.state]);

    const getDocumentDetailsById = async () => {
        try {
            const response = await getDocumentById({
                doc_id: decodedContentId
            });
            // console.log('Document Details:', response);

            if (response.success && response.data) {
                const docData = response.data;
                setDocumentData({
                    name: docData.doc_name || 'Untitled Document',
                    pdfUrl: docData.doc_url || '',
                    points: docData.earning_points !== undefined && docData.earning_points !== null ? docData.earning_points.toString() : '0'
                });

                // Log the updated document data
                // Removed multi-line console.log
            } else {
                console.error('Failed to fetch document details:', response.message);
                toast.error('Failed to load document details');
            }
        } catch (error) {
            console.error('Error fetching document details:', error);
            toast.error('Error loading document details');
        }
    };

    useEffect(() => {
        if (decodedContentId) {
            getDocumentDetailsById();
        }
    }, [decodedContentId]);

    // Reset errors when modal opens
    useEffect(() => {
        if (showEditModal) {
            setErrors({
                documentName: '',
                documentFile: '',
                points: ''
            });
        }
    }, [showEditModal]);

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg m-3" role="alert">
                {error}
            </div>
        );
    }

    const handleDocumentRemove = () => {
        setDocumentData(prev => ({
            ...prev,
            pdfUrl: ''
        }));
    };

    const handleDocumentUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                setErrors(prev => ({
                    ...prev,
                    documentFile: 'Please upload a PDF file only'
                }));
                return;
            }

            if (file.size > 10 * 1024 * 1024) { // 10MB
                setErrors(prev => ({
                    ...prev,
                    documentFile: 'File size should not exceed 10MB'
                }));
                return;
            }

            setErrors(prev => ({
                ...prev,
                documentFile: ''
            }));
            setUploadDoc(file);
            setDocumentData(prev => ({
                ...prev,
                pdfUrl: URL.createObjectURL(file)
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {
            documentName: '',
            documentFile: '',
            points: ''
        };
        let isValid = true;

        // Validate document name
        if (!documentData.name.trim()) {
            newErrors.documentName = 'Document Name is required';
            isValid = false;
        } else if (documentData.name.trim().length > 100) {
            newErrors.documentName = 'Document Name cannot exceed 100 characters';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSaveChanges = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        // console.log('Saving document changes...');
        
        try {
            const formData = new FormData();
            formData.append('doc_id', decodedContentId);
            formData.append('doc_name', documentData.name);
            formData.append('earning_points', documentData.points || '0');

            if (uploadDoc !== null) {
                try {
                    if (typeof uploadDoc === 'string' && uploadDoc.startsWith('blob:')) {
                        // console.log('Converting blob URL to File object');
                        const fileObject = await urlToFile(uploadDoc);
                        formData.append('doc_url', fileObject);
                        // Removed multi-line console.log
                    } else {
                        formData.append('doc_url', uploadDoc);
                        // console.log('Using existing file object');
                    }
                    formData.append('file_changed', 'true');
                } catch (error) {
                    console.error('Error processing file:', error);
                    toast.error('Error processing the file');
                    setIsSaving(false); // Stop loading on error
                    return;
                }
            } else {
                formData.append('doc_url', documentData.pdfUrl);
                formData.append('file_changed', 'false');
            }

            const response = await editDocument(formData);

            if (response.success) {
                // Artificial delay for better UX (2 seconds)
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                toast.success('Document updated successfully!');
                
                try {
                    const updatedDoc = await getDocumentById({
                        doc_id: decodedContentId
                    });
                    
                    if (updatedDoc.success && updatedDoc.data) {
                        // console.log('Updated document details:', updatedDoc.data);
                        setDocumentData({
                            name: updatedDoc.data.doc_name || 'Untitled Document',
                            pdfUrl: updatedDoc.data.doc_url || '',
                            points: updatedDoc.data.earning_points !== undefined && updatedDoc.data.earning_points !== null ? updatedDoc.data.earning_points.toString() : '0'
                        });
                        setUploadDoc(null);
                        setShowEditModal(false);
                    } else {
                        console.error('Failed to fetch updated document details');
                        toast.warning('Document updated but failed to refresh display');
                    }
                } catch (error) {
                    console.error('Error fetching updated document:', error);
                    toast.warning('Document updated but failed to refresh display');
                }
            } else {
                toast.error(response.data?.message || 'Failed to update document');
            }
        } catch (error) {
            console.error('Error updating document:', error);
            toast.error('An error occurred while updating the document');
        } finally {
            setIsSaving(false); // Stop loading whether success or error
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setErrors({
            documentName: '',
            documentFile: ''
        });
        // Reset the form to original data
        getDocumentDetailsById();
    };

    const handleEditClick = () => {
        if (!permissions.course_module_content_management_edit) {
            toast.warning("You don't have permission to edit documents");
            return;
        }
        setShowEditModal(true);
    };

    const encodedCourseId = encodeData(decodedCourseId);
    const encodedModuleId = encodeData(decodedModuleId);
    const backToContentUrl = `/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}`;

    return (
        <>
            <div className="px-4 md:px-8 py-5 max-w-6xl mx-auto">
                {/* Header: back, title, document name, edit */}
                <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <a
                            href={backToContentUrl}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 no-underline shrink-0"
                            title="Back to Manage Content"
                        >
                            <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
                        </a>
                        <div>
                            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight mb-0">
                                Course Document
                            </h1>
                            <p className="text-sm text-gray-500 mt-1 mb-0">
                                {documentData.name}
                            </p>
                        </div>
                    </div>
                    {permissions.course_module_content_management_edit && (
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                            onClick={handleEditClick}
                            disabled={!permissions.course_module_content_management_edit}
                            title={!permissions.course_module_content_management_edit ? "You don't have permission to edit documents" : "Edit Document"}
                        >
                            <Icon icon="fluent:edit-24-regular" width={18} height={18} />
                            <span>Edit Document</span>
                        </button>
                    )}
                </div>

                {/* Document viewer card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800">Document preview</span>
                        {/* Points display - hidden per design
                        {documentData.points !== undefined && documentData.points !== '' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                <Icon icon="fluent:star-24-regular" width={14} height={14} />
                                {documentData.points} pts
                            </span>
                        )}
                        */}
                    </div>
                    <div className="p-4 md:p-6">
                        {documentData.pdfUrl ? (
                            <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50/50">
                                <div className="w-full" style={{ minHeight: '72vh' }}>
                                    <iframe
                                        src={documentData.pdfUrl}
                                        title="Document Viewer"
                                        className="w-full border-0 rounded-lg block"
                                        style={{ minHeight: '72vh' }}
                                        allowFullScreen
                                    >
                                        This browser does not support PDFs. Please download the PDF to view it.
                                    </iframe>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 text-center">
                                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                    <Icon icon="fluent:document-24-regular" width={28} height={28} className="text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium mb-1">No document available</p>
                                <p className="text-sm text-gray-500 mb-0">Upload a document via Edit Document to view it here.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Document Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                                    aria-label="Back"
                                >
                                    <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
                                </button>
                                <h5 className="text-lg font-semibold mb-0">Edit Document</h5>
                            </div>
                            <button 
                                type="button" 
                                className="text-gray-400 hover:text-gray-600"
                                onClick={handleCloseModal}
                                aria-label="Close"
                            >
                                <Icon icon="mdi:close" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            {/* Document Name Field */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-font-primary mb-1.5">Document Name *</label>
                                <input
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.documentName ? 'border-red-500' : 'border-input-border'}`}
                                    value={documentData.name}
                                    onChange={(e) => {
                                        setDocumentData(prev => ({
                                            ...prev,
                                            name: e.target.value
                                        }));
                                        if (errors.documentName) {
                                            setErrors(prev => ({
                                                ...prev,
                                                documentName: ''
                                            }));
                                        }
                                    }}
                                    maxLength={100}
                                    placeholder="Enter document name"
                                />
                                {errors.documentName ? (
                                    <div className="text-red-500 text-sm mt-1">{errors.documentName}</div>
                                ) : (
                                    <small className="text-gray-500">Maximum 100 characters allowed ({100 - documentData.name.length} remaining)</small>
                                )}
                            </div>

                            {/* Points field hidden in this modal – handled from module content */}

                            {/* Document Upload */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-3">
                                <div className="p-4">
                                    {!documentData.pdfUrl ? (
                                        <div
                                            className="upload-area relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100"
                                            onClick={() => document.getElementById('documentEditUpload')?.click()}
                                        >
                                            <input
                                                type="file"
                                                id="documentEditUpload"
                                                className="hidden"
                                                accept=".pdf"
                                                onChange={handleDocumentUpload}
                                            />
                                            <div className="flex flex-col items-center justify-center text-center gap-1">
                                                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-1">
                                                    <Icon icon="fluent:document-pdf-24-regular" width={26} height={26} />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">
                                                    Drag &amp; drop PDF or click to browse
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    Only PDF files up to 10MB are supported
                                                </span>
                                            </div>
                                            {errors.documentFile && (
                                                <div className="text-red-500 text-xs mt-2">{errors.documentFile}</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
                                                    <Icon icon="fluent:document-pdf-24-regular" width={22} height={22} />
                                                </div>
                                                <div className="text-left">
                                                    <p className="text-sm font-medium text-gray-900 mb-0">
                                                        Current document attached
                                                    </p>
                                                    <p className="text-xs text-gray-500 mb-0">
                                                        You can change the document if needed.
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => document.getElementById('documentEditUpload')?.click()}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary text-primary text-xs font-medium hover:bg-primary/10"
                                            >
                                                <Icon icon="fluent:arrow-sync-24-regular" width={16} height={16} />
                                                <span>Change document</span>
                                            </button>
                                            <input
                                                type="file"
                                                id="documentEditUpload"
                                                className="hidden"
                                                accept=".pdf"
                                                onChange={handleDocumentUpload}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Current Document Preview */}
                            {documentData.pdfUrl && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-3">
                                    <div className="p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h6 className="mb-0 text-sm font-semibold text-gray-900">Current Document Preview</h6>
                                        </div>
                                        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={documentData.pdfUrl}
                                                title="Document Preview"
                                                className="absolute top-0 left-0 w-full h-full border-0 rounded"
                                                allowFullScreen
                                            >
                                            </iframe>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button 
                                type="button" 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                                onClick={handleCloseModal}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                                onClick={handleSaveChanges}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" role="status" aria-hidden="true"></span>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CourseDocument;
